// React imports
import React, { useEffect } from 'react';

// Material UI imports
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

// Tiptap imports
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { mergeAttributes, findChildren, Node, Extension } from '@tiptap/core';
import Paragraph from '@tiptap/extension-paragraph';
import { TextStyle } from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import { richPasteExtensions, createImagePasteHandler, resolveStyleBlocks } from '../utils/richPaste.js';

const DivParagraph = Paragraph.extend({
    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});

// Link is provided by StarterKit as a Mark, not a Node
// Removed custom Link Node to avoid conflict with StarterKit's Link Mark

// FontSize Extension
const FontSize = Extension.create({
    name: 'fontSize',

    addOptions() {
        return {
            types: ['textStyle'],
        }
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize || null,
                        renderHTML: attributes => {
                            if (!attributes.fontSize) {
                                return {}
                            }
                            return { style: `font-size: ${attributes.fontSize}` }
                        },
                    },
                },
            },
        ]
    },

    addCommands() {
        return {
            setFontSize:
                size =>
                    ({ chain }) => {
                        return chain()
                            .setMark('textStyle', { fontSize: size })
                            .run()
                    },
            unsetFontSize:
                () =>
                    ({ chain }) => {
                        return chain()
                            .setMark('textStyle', { fontSize: null })
                            .removeEmptyTextStyle()
                            .run()
                    },
        }
    },
});

// 2. Editor Setup
const editorExtensions = [
    StarterKit.configure({
        paragraph: false, // Disable default paragraph
    }),
    DivParagraph,
    TextStyle,
    FontFamily,
    FontSize,
    // Lets a reply accept a pasted spreadsheet as a table and a screenshot as an image,
    // the same as the quote builder - this editor is a separate instance with its own
    // extension list, which is why it did not follow along on its own.
    ...richPasteExtensions,
    // Link is included in StarterKit as a Mark
];

// 3. Toolbar Component
const Toolbar = ({ editor, style }) => {
    if (!editor) return null;

    const buttonStyle = {
        padding: '8px',
        margin: '0 1px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        fontSize: '18px',
        color: '#5f6368',
        minWidth: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s ease',
    };

    const activeButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#e8f0fe',
        color: '#1a73e8',
    };

    const hoverStyle = {
        backgroundColor: '#f1f3f4',
    };

    const createButton = (onClick, isActive, title, children, disabled = false) => (
        <button
            style={isActive ? activeButtonStyle : buttonStyle}
            onClick={onClick}
            disabled={disabled}
            title={title}
            onMouseEnter={(e) => {
                if (!isActive && !disabled) {
                    e.target.style.backgroundColor = hoverStyle.backgroundColor;
                }
            }}
            onMouseLeave={(e) => {
                if (!isActive && !disabled) {
                    e.target.style.backgroundColor = buttonStyle.backgroundColor;
                }
            }}
        >
            {children}
        </button>
    );

    return (
        <div style={{
            border: '1px solid #dadce0',
            padding: '8px 16px',
            backgroundColor: '#fff',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2px',
            alignItems: 'center',
            minHeight: '56px',
            width: '100%',
            ...style
        }}>
            {/* Font Family Selector */}
            <Select
                size="small"
                value={editor.getAttributes('textStyle').fontFamily || 'Poppins'}
                onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
                sx={{
                    minWidth: 120,
                    marginRight: '8px',
                    '& .MuiSelect-select': {
                        padding: '4px 8px',
                        fontFamily: 'Poppins, sans-serif',
                    }
                }}
                MenuProps={{
                    style: {
                        zIndex: 100000000000000,
                    },
                }}
                renderValue={(value) => {
                    const font = value || 'Poppins';
                    return <span style={{ fontFamily: font, fontWeight: 500 }}>{font}</span>;
                }}
            >
                {[
                    'Poppins',
                    'Arial',
                    'Helvetica',
                    'Verdana', 
                    'Tahoma',
                    'Trebuchet MS',
                    'Georgia',
                    'Garamond',
                    'Palatino',
                    'Bookman',
                    'Comic Sans MS'
                ].map(font => (
                    <MenuItem key={font} value={font}>
                        <span style={{fontFamily: font}}>{font}</span>
                    </MenuItem>
                ))}
                <MenuItem value="Times New Roman">
                    <span style={{fontFamily: 'Times New Roman'}}>Times New Roman</span>
                </MenuItem>
                <MenuItem value="Courier New">
                    <span style={{fontFamily: 'Courier New'}}>Courier New</span>
                </MenuItem>
            </Select>
            
            {/* Font Size Selector */}
            <Select
                size="small"
                value={editor.getAttributes('textStyle').fontSize || '12px'}
                onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
                sx={{
                    minWidth: 80,
                    marginRight: '8px',
                    '& .MuiSelect-select': {
                        padding: '4px 8px',
                    }
                }}
                MenuProps={{
                    style: {
                        zIndex: 1000000000000,
                    },
                }}
            >
                {[
                    '10px',
                    '11px', 
                    '12px',
                    '13px',
                    '14px',
                    '16px',
                    '18px',
                    '20px',
                    '24px',
                    '28px',
                    '32px',
                    '36px',
                    '48px'
                ].map(size => (
                    <MenuItem key={size} value={size}>
                        <span style={{fontSize: '13px', fontFamily: 'Arial'}}>{size}</span>
                    </MenuItem>
                ))}
            </Select>

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Text Formatting */}
            {createButton(
                () => editor.chain().focus().toggleBold().run(),
                editor.isActive('bold'),
                'Bold (Ctrl+B)',
                <span style={{ fontWeight: 'bold' }}>B</span>
            )}
            {createButton(
                () => editor.chain().focus().toggleItalic().run(),
                editor.isActive('italic'),
                'Italic (Ctrl+I)',
                <span style={{ fontStyle: 'italic' }}>I</span>
            )}
            {createButton(
                () => editor.chain().focus().toggleStrike().run(),
                editor.isActive('strike'),
                'Strikethrough',
                <span style={{ textDecoration: 'line-through' }}>S</span>
            )}
            {createButton(
                () => editor.chain().focus().toggleCode().run(),
                editor.isActive('code'),
                'Monospace',
                <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>M</span>
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Lists */}
            {createButton(
                () => editor.chain().focus().toggleBulletList().run(),
                editor.isActive('bulletList'),
                'Bullet list',
                <span style={{ fontSize: '16px' }}>•</span>
            )}
            {createButton(
                () => editor.chain().focus().toggleOrderedList().run(),
                editor.isActive('orderedList'),
                'Numbered list',
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>1.</span>
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Headings */}
            {createButton(
                () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
                editor.isActive('heading', { level: 1 }),
                'Heading 1',
                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>H1</span>
            )}
            {createButton(
                () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
                editor.isActive('heading', { level: 2 }),
                'Heading 2',
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>H2</span>
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Blockquote */}
            {createButton(
                () => editor.chain().focus().toggleBlockquote().run(),
                editor.isActive('blockquote'),
                'Quote',
                <span style={{ fontSize: '18px' }}>"</span>
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Code Block */}
            {createButton(
                () => editor.chain().focus().toggleCodeBlock().run(),
                editor.isActive('codeBlock'),
                'Code block',
                <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>{'<>'}</span>
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Horizontal Rule */}
            {createButton(
                () => editor.chain().focus().setHorizontalRule().run(),
                false,
                'Horizontal rule',
                <span style={{ fontSize: '16px' }}>―</span>
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Undo/Redo */}
            {createButton(
                () => editor.chain().focus().undo().run(),
                false,
                'Undo (Ctrl+Z)',
                <span style={{ fontSize: '16px' }}>↶</span>,
                !editor.can().undo()
            )}
            {createButton(
                () => editor.chain().focus().redo().run(),
                false,
                'Redo (Ctrl+Y)',
                <span style={{ fontSize: '16px' }}>↷</span>,
                !editor.can().redo()
            )}
        </div>
    )
};

function ReplyEditor(props) {
    const { onBlur, initialContent, editable = true, toolbarStyle, onUpdate, onUploadImage } = props;
    const lastInitialContentRef = React.useRef(initialContent);
    const isUpdatingProgrammaticallyRef = React.useRef(false);
    const onUpdateRef = React.useRef(onUpdate);
    
    // Keep onUpdate ref in sync
    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    // handlePaste is built before useEditor returns, so it reaches the instance through a
    // ref rather than closing over a variable that is still undefined.
    const editorRef = React.useRef(null);
    const editor = useEditor({
        extensions: editorExtensions,
        content: initialContent || '',
        editorProps: {
            attributes: {
                style: "font-family: 'Poppins', sans-serif; font-size: 12px;",
            },
            // Same as the quote builder: resolve the pasted stylesheet before parsing.
            transformPastedHTML: (html) => resolveStyleBlocks(html),
            handlePaste: createImagePasteHandler({
                getEditor: () => editorRef.current,
                onUploadImage,
                onChanged: ({ html }) => onUpdateRef.current && onUpdateRef.current({ html }),
            }),
        },
        onUpdate: ({ editor }) => {
            // Completely skip onUpdate if we're programmatically updating
            if (isUpdatingProgrammaticallyRef.current) {
                return;
            }
            
            // Use ref to avoid stale closure
            if (onUpdateRef.current) {
                const html = editor.getHTML();
                onUpdateRef.current({ html });
            }
        },
    });
    
    useEffect(() => {
        // Only update editor content if initialContent changed externally
        if (!editor || initialContent === undefined) {
            return;
        }
        
        // Skip if content hasn't changed (exact match)
        if (lastInitialContentRef.current === initialContent) {
            return;
        }
        
        // Skip if we're already updating (prevent recursive calls)
        if (isUpdatingProgrammaticallyRef.current) {
            lastInitialContentRef.current = initialContent;
            return;
        }
        
        const currentHtml = editor.getHTML();
        const normalizedCurrent = currentHtml.trim();
        const normalizedInitial = (initialContent || '').trim();
        
        // Only update if content is actually different
        if (normalizedCurrent !== normalizedInitial) {
            // Set flag synchronously BEFORE updating to prevent onUpdate from firing
            isUpdatingProgrammaticallyRef.current = true;
            
            // Update lastInitialContentRef immediately to prevent re-triggering
            lastInitialContentRef.current = initialContent;
            
            // Update editor content
            try {
                editor.commands.setContent(initialContent || '');
            } catch (e) {
                console.error('Error setting editor content:', e);
                isUpdatingProgrammaticallyRef.current = false;
                return;
            }
            
            // Reset flag after editor has processed the update
            setTimeout(() => {
                isUpdatingProgrammaticallyRef.current = false;
            }, 200);
        } else {
            // Content matches, just update the ref
            lastInitialContentRef.current = initialContent;
        }
    }, [editor, initialContent]);

    if (editor) {
        editorRef.current = editor;
        editor.setEditable(editable);
    }

    if (!editor) return null;

    return (
        <div onClick={() => editor.chain().focus().run()} style={{ cursor: 'text' }}>
            {editable && (
                <Toolbar 
                    editor={editor} 
                    style={toolbarStyle}
                />
            )}
            <EditorContent 
                editor={editor}
                onBlur={() => {
                    if (onBlur) {
                        let html = editor.getHTML();
                        // Ensure the HTML has Poppins as default font-family
                        if (!html.includes('font-family') && !html.includes('fontFamily')) {
                            html = `<div style="font-family: 'Poppins', sans-serif;">${html}</div>`;
                        }
                        onBlur({ html });
                    }
                }}
                style={{ 
                    border: '1px solid #e5e7eb', 
                    borderTop: !editable ? '1px solid #e5e7eb' : 'none',
                    backgroundColor: '#ffffff',
                    padding: '16px', 
                    borderRadius: !editable ? '8px' : '0 0 8px 8px',
                    minHeight: '300px',
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    lineHeight: '1.5',
                    fontSize: '12px',
                    fontFamily: "'Poppins', sans-serif",
                    outline: 'none',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }} 
            />
            <style>
                {`
                    .ProseMirror {
                        outline: none !important;
                        border: none !important;
                        font-family: 'Poppins', sans-serif !important;
                        font-size: 12px !important;
                    }
                    .ProseMirror:focus {
                        outline: none !important;
                        border: none !important;
                    }
                    .ProseMirror * {
                        outline: none !important;
                    }
                `}
            </style>
        </div>
    );
}

export default ReplyEditor;
